import { exactSampleCount, TICKS_PER_FRAME } from '../core/time';
import type { AudioBlock, AudioDecodeRequest, AudioResampler, AudioSourceDecoder, CanonicalPcm, ResampleRequest } from './l3-types';
import { LabError, throwIfAborted } from './l3-errors';

const BLOCK_FRAMES = 8_192;

function assertAudioContext(): void {
  if (typeof OfflineAudioContext === 'undefined') throw new LabError('WEB_AUDIO_UNAVAILABLE', 'audio-decode', 'OfflineAudioContext is unavailable.');
}

async function decodeTo48k(request: AudioDecodeRequest, signal: AbortSignal): Promise<AudioBuffer> {
  assertAudioContext();
  throwIfAborted(signal, 'audio-decode');
  const decodeContext = new OfflineAudioContext(2, 1, 48_000);
  let decoded: AudioBuffer;
  try {
    decoded = await decodeContext.decodeAudioData(request.source.data);
  } catch (cause) {
    throw new LabError('AUDIO_DECODE_FAILED', 'audio-decode', cause instanceof Error ? cause.message : String(cause));
  }
  throwIfAborted(signal, 'audio-decode');
  if (decoded.numberOfChannels === 0 || decoded.numberOfChannels > 2) throw new LabError('UNSUPPORTED_CHANNEL_COUNT', 'audio-decode', `Expected one or two audio channels, got ${decoded.numberOfChannels}.`);
  if (decoded.sampleRate === 48_000) return decoded;

  const outputFrames = Math.ceil(decoded.duration * 48_000);
  const renderContext = new OfflineAudioContext(decoded.numberOfChannels, outputFrames, 48_000);
  const source = renderContext.createBufferSource();
  source.buffer = decoded;
  source.connect(renderContext.destination);
  source.start();
  try {
    return await renderContext.startRendering();
  } catch (cause) {
    throw new LabError('AUDIO_RESAMPLE_FAILED', 'audio-resample', cause instanceof Error ? cause.message : String(cause));
  }
}

export class WebAudioSourceDecoder implements AudioSourceDecoder {
  async *decode(request: AudioDecodeRequest, signal: AbortSignal): AsyncIterable<AudioBlock> {
    const buffer = await decodeTo48k(request, signal);
    for (let startSample = 0; startSample < buffer.length; startSample += BLOCK_FRAMES) {
      throwIfAborted(signal, 'audio-decode');
      const endSample = Math.min(buffer.length, startSample + BLOCK_FRAMES);
      const first = buffer.getChannelData(0).slice(startSample, endSample);
      const second = buffer.numberOfChannels === 1
        ? first.slice()
        : buffer.getChannelData(1).slice(startSample, endSample);
      yield { sampleRate: buffer.sampleRate, channels: [first, second], startSample };
    }
  }
}

function envelope(index: number, sampleCount: number, request: ResampleRequest): number {
  const fadeInSamples = request.fadeInFrames * TICKS_PER_FRAME;
  const fadeOutSamples = request.fadeOutFrames * TICKS_PER_FRAME;
  if (fadeInSamples + fadeOutSamples > sampleCount) throw new LabError('AUDIO_FADE_RANGE_INVALID', 'audio-process', 'Audio fades exceed the selected duration.');
  const gain = request.volumePercent / 100;
  const fadeIn = fadeInSamples === 0 || index >= fadeInSamples ? 1 : index / (fadeInSamples - 1);
  const fadeOut = fadeOutSamples === 0 || index < sampleCount - fadeOutSamples ? 1 : (sampleCount - 1 - index) / (fadeOutSamples - 1);
  return gain * fadeIn * fadeOut;
}

function clamp(value: number): number {
  return Math.max(-1, Math.min(1, value));
}

export class Native48kResampler implements AudioResampler {
  async process(input: AsyncIterable<AudioBlock>, request: ResampleRequest, signal: AbortSignal): Promise<CanonicalPcm> {
    if (!Number.isSafeInteger(Number(request.sourceStartTicks)) || request.sourceStartTicks < 0n) throw new LabError('AUDIO_OFFSET_INVALID', 'audio-process', 'Audio selection offset is invalid.');
    if (!Number.isSafeInteger(request.volumePercent) || request.volumePercent < 0 || request.volumePercent > 100) throw new LabError('AUDIO_VOLUME_INVALID', 'audio-process', 'Audio volume must be an integer between 0 and 100.');
    const targetCount = exactSampleCount(request.frameCount);
    const start = Number(request.sourceStartTicks);
    const end = start + targetCount;
    const left = new Float32Array(targetCount);
    const right = new Float32Array(targetCount);
    let copied = 0;
    let lastAvailableSample = -1;

    for await (const block of input) {
      throwIfAborted(signal, 'audio-process');
      if (block.sampleRate !== 48_000) throw new LabError('AUDIO_RESAMPLE_FAILED', 'audio-process', `Expected 48 kHz blocks, got ${block.sampleRate}.`);
      if (block.channels.length !== 2 || block.channels[0]!.length !== block.channels[1]!.length) throw new LabError('AUDIO_BLOCK_INVALID', 'audio-process', 'Audio block must contain two equal-length channels.');
      const blockEnd = block.startSample + block.channels[0]!.length;
      lastAvailableSample = Math.max(lastAvailableSample, blockEnd - 1);
      const overlapStart = Math.max(start, block.startSample);
      const overlapEnd = Math.min(end, blockEnd);
      for (let sourceIndex = overlapStart; sourceIndex < overlapEnd; sourceIndex += 1) {
        const outputIndex = sourceIndex - start;
        const blockIndex = sourceIndex - block.startSample;
        const gain = envelope(outputIndex, targetCount, request);
        left[outputIndex] = clamp(block.channels[0]![blockIndex]! * gain);
        right[outputIndex] = clamp(block.channels[1]![blockIndex]! * gain);
        copied += 1;
      }
    }
    if (copied !== targetCount) {
      const availableAfterStart = Math.max(0, lastAvailableSample - start + 1);
      throw new LabError('SOURCE_TOO_SHORT', 'audio-process', `Audio selection requires ${targetCount} samples but only ${availableAfterStart} are available.`);
    }
    return { sampleRate: 48_000, channels: [left, right], sampleCount: targetCount };
  }
}

export async function decodeCanonicalAudio(request: AudioDecodeRequest & ResampleRequest, signal: AbortSignal): Promise<CanonicalPcm> {
  const decoder = new WebAudioSourceDecoder();
  const resampler = new Native48kResampler();
  return resampler.process(decoder.decode(request, signal), request, signal);
}
